import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import ForceGraph2D from "react-force-graph-2d";

const SkillGraph = ({ graphData }) => {
  const fgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 800 });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const totalWeight = useMemo(() => {
    if (!graphData?.nodes) return 1;
    return graphData.nodes.reduce((sum, n) => sum + (n.weight || 0), 0);
  }, [graphData]);

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setDimensions({ width, height });
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!graphData || !fgRef.current) return;
    fgRef.current.d3Force("charge").strength(-600);
    fgRef.current.d3Force("link").distance(150);
  }, [graphData]);


  const handleEngineStop = useCallback(() => {
    fgRef.current?.zoomToFit(300, 40);
  }, []);

  const handleNodeHover = useCallback((node) => {
    setHoveredNode(node || null);
    document.body.style.cursor = node ? "pointer" : "default";
    if (node && fgRef.current) {
      const screenPos = fgRef.current.graph2ScreenCoords(node.x, node.y);
      setTooltipPos({ x: screenPos.x, y: screenPos.y });
    }
  }, []);

  const nodeCanvasObject = useCallback(
    (node, ctx) => {
      if (!isFinite(node.x) || !isFinite(node.y)) return;

      const r = (node.val || 4) * 3.5;
      const isHovered = hoveredNode?.id === node.id;
      const color = node.color || "#94a3b8";
      const label = node.label || node.id;

      // Unique phase per node so each floats independently
      const phase = String(node.id)
        .split("")
        .reduce((acc, c) => acc + c.charCodeAt(0), 0);
      const floatAmp = r * 0.22;
      const floatY = Math.sin(Date.now() / 1400 + phase) * floatAmp;

      const dx = node.x;
      const dy = node.y + floatY;

      // Glow
      const glowRadius = isHovered ? r * 2 : r * 1.6;
      const glow = ctx.createRadialGradient(dx, dy, r * 0.5, dx, dy, glowRadius);
      glow.addColorStop(0, color + (isHovered ? "77" : "55"));
      glow.addColorStop(1, color + "00");
      ctx.beginPath();
      ctx.arc(dx, dy, glowRadius, 0, 2 * Math.PI);
      ctx.fillStyle = glow;
      ctx.fill();

      // Circle
      ctx.beginPath();
      ctx.arc(dx, dy, isHovered ? r * 1.1 : r, 0, 2 * Math.PI);
      ctx.fillStyle = color + (isHovered ? "ff" : "dd");
      ctx.fill();

      // Orbit ring + revolving dot on hover
      if (isHovered) {
        const orbitR = r * 1.9;
        const angle = (Date.now() / 600) % (2 * Math.PI);

        // Dashed orbit ring
        ctx.beginPath();
        ctx.arc(dx, dy, orbitR, 0, 2 * Math.PI);
        ctx.strokeStyle = color + "44";
        ctx.lineWidth = 0.8;
        ctx.setLineDash([3, 5]);
        ctx.stroke();
        ctx.setLineDash([]);

        // Revolving dot
        const dotX = dx + orbitR * Math.cos(angle);
        const dotY = dy + orbitR * Math.sin(angle);
        const dotR = r * 0.28;
        const dotGlow = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, dotR * 2.5);
        dotGlow.addColorStop(0, color + "cc");
        dotGlow.addColorStop(1, color + "00");
        ctx.beginPath();
        ctx.arc(dotX, dotY, dotR * 2.5, 0, 2 * Math.PI);
        ctx.fillStyle = dotGlow;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(dotX, dotY, dotR, 0, 2 * Math.PI);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
      }

      // Label below
      const fontSize = Math.max(4, r * 0.55);
      ctx.font = `600 ${fontSize}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = isHovered ? "#ffffff" : "rgba(255,255,255,0.88)";
      ctx.fillText(label, dx, dy + r * 1.8);
    },
    [hoveredNode]
  );

  const nodeCanvasObjectMode = useCallback(() => "replace", []);

  const pct = hoveredNode
    ? ((hoveredNode.weight / totalWeight) * 100).toFixed(1)
    : null;

  return (
    <div
      ref={containerRef}
      className="skill-graph-container"
      aria-label="Interactive skill graph"
      role="img"
      style={{ position: "relative" }}
    >
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        width={dimensions.width}
        height={dimensions.height}
        backgroundColor="rgba(0,0,0,0)"
        nodeCanvasObject={nodeCanvasObject}
        nodeCanvasObjectMode={nodeCanvasObjectMode}
        nodeLabel={null}
        linkColor={() => "rgba(150,150,150,0.25)"}
        linkWidth={1}
        onNodeHover={handleNodeHover}
        onEngineStop={handleEngineStop}
        enableNodeDrag={true}
        enableZoomInteraction={false}
        showNavInfo={false}
        warmupTicks={120}
        cooldownTicks={0}
        autoPauseRedraw={false}
      />

      {hoveredNode && (
        <div
          className="skill-tooltip"
          style={{ left: tooltipPos.x, top: tooltipPos.y }}
        >
          <span className="skill-tooltip-name">{hoveredNode.label}</span>
          <span className="skill-tooltip-category">{hoveredNode.category}</span>
          <span className="skill-tooltip-pct">{pct}%</span>
        </div>
      )}
    </div>
  );
};

export default SkillGraph;
